import { makeAutoObservable } from 'mobx';
import type PulseApi from '../../../api/PulseApi';
import { serialize } from '../../../store/utils/localstorage';
import { onWarning } from '../../../utils/errors';
import type AccountStore from '../../account/store/AccountStore';
import type { Role } from '../../users/store/User';
import SigningIn from '../login/store/SigningIn';
import ResetPassword from '../reset/store/ResetPassword';
import AuthSession, { type HasRoleOptions } from './AuthSession';

export default class AuthStore {
  session?: AuthSession = undefined;
  isRestoring = true;

  constructor(
    private readonly api: PulseApi,
    private readonly accountStore: AccountStore,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.tryRestoreSession().catch(onWarning);

    this.api.onTokenRefreshed((token) => {
      this.session?.update(token);
      this.setSession(this.session);
    });
  }

  get isLoggedIn(): boolean {
    return Boolean(this.session);
  }

  createResetPassword(): ResetPassword {
    return new ResetPassword(this.performResetPassword);
  }

  createSigningIn(): SigningIn {
    return new SigningIn(this.performSignIn);
  }

  async impersonate(userId: string) {
    this.setIsRestoring(true);

    const authInfo = await this.api.users.generateAuthInfo(userId);
    this.session?.impersonate(authInfo);
    await this.accountStore.loadAccount();
  }

  setSession(session?: AuthSession) {
    this.session = session;
    if (session) {
      this.api.setToken(session);
    }
  }

  async signOut() {
    if (this.session?.isImpersonated) {
      this.session.finishImpersonation();
      await this.accountStore.loadAccount();
      return;
    }

    this.setSession(undefined);
    serialize('session', null);
  }

  userHasRole(role: Role, options?: HasRoleOptions): boolean {
    return this.session?.hasRole(role, options) || false;
  }

  private async performResetPassword(email: string) {
    await this.api.auth.resetPassword(email);
  }

  private async performSignIn(email: string, password: string) {
    const clearedEmail = email.trim().toLowerCase();

    const dto = await this.api.auth.signIn(clearedEmail, password);
    if (!dto.role) {
      throw new Error('Wrong API response: role is empty');
    }

    const session = new AuthSession(dto);
    session.serialize();

    this.setSession(session);
    await this.accountStore.loadAccount();
  }

  private setIsRestoring(value: boolean) {
    this.isRestoring = value;
  }

  private async tryRestoreSession() {
    this.setIsRestoring(true);

    try {
      const session = AuthSession.restore();
      if (!session) {
        return;
      }

      const dto = await this.api.auth.refreshIdToken(session.refreshToken);
      session.update(dto);
      this.setSession(session);
      session.serialize();
      await this.accountStore.loadAccount();
    } finally {
      this.setIsRestoring(false);
    }
  }
}
